// @flow
import { Fragment } from 'react';
import styled from '@emotion/styled';
import { SPACING } from '@tablekit/theme';
import {
  MOBILE_BREAKPOINT,
  GRID_GUTTER,
  MAX_WIDTH,
  LAPTOP_BREAKPOINT
} from 'utils/theme';
import type {
  BlokComponentPropsType,
  BlokComponentType,
  ImageType,
  LinkedImageType,
  UrlType
} from 'components/types';
import Title from 'styled/Title';
import renderComponent from './renderComponent';
import LinkedImage from './LinkedImage';
import Image from '../Image';
import Link, { isValidLink } from '../Link';
import CTAButton from '../CTAButton';
import * as componentKeys from '../../componentKeys';
import columnWidth from '../../utils/columnWidth';

const mobileHorizontalGutter = 12;
const mobileVerticalGutter = 8;

const FilterContent = styled.div`
  filter: url("data:image/svg+xml;utf8,<svg height='0' xmlns='http://www.w3.org/2000/svg'><filter id='svgBlur' x='-5%' y='-5%' width='110%' height='110%'><feGaussianBlur in='SourceGraphic' stdDeviation='5'/></filter></svg>#svgBlur");
  filter: progid: DXImageTransform.Microsoft.Blur(PixelRadius = '4');
  filter: blur(4px);
  opacity: 0;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  transition: all 0.3s ease-in-out;
  background: white;
`;

const Content = styled.div`
  transition: all 0.3s ease-in-out;
  opacity: 1;
`;

const ButtonPositioner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.6s ease-in-out;
  z-index: 2;

  @media ${MOBILE_BREAKPOINT} {
    opacity: 1;
    position: relative;
    transform: none;
    left: auto;
    top: auto;
    display: flex;
    justify-content: center;
    margin-top: ${SPACING.L6};
    margin-bottom: ${SPACING.L5};
  }
`;

const Positioner = styled.div`
  position: relative;

  @media ${LAPTOP_BREAKPOINT} {
    &:hover {
      > ${FilterContent}, > ${ButtonPositioner} {
        opacity: 1;
      }
      ${Content} {
        opacity: 0;
      }
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  max-width: ${MAX_WIDTH};
  margin: -${GRID_GUTTER} -${GRID_GUTTER} -${GRID_GUTTER} 0;
  padding-top: ${GRID_GUTTER};
  @media ${MOBILE_BREAKPOINT} {
    margin: -${mobileVerticalGutter}px 0;
    padding: ${mobileVerticalGutter}px ${GRID_GUTTER};
  }

  & > * {
    box-sizing: border-box;
    border: 0 solid transparent;
    border-width: 0 ${GRID_GUTTER} ${GRID_GUTTER} 0;
    width: ${columnWidth(2)}%;
    @media ${MOBILE_BREAKPOINT} {
      width: ${columnWidth(6)}%;
      border-width: 0 ${mobileHorizontalGutter}px ${mobileVerticalGutter}px
        ${mobileHorizontalGutter}px;
    }
  }
`;

const OverflowHandler = styled.div`
  overflow: hidden;

  @media ${MOBILE_BREAKPOINT} {
    margin: 0 -${SPACING.L7};
  }
`;

const ImageBlock = ({
  blok: {
    content: elements,
    // $FlowFixMe flow claims title type is not defined
    title,
    link,
    link_text: linkText,
    ...others
  }
}: BlokComponentPropsType<{
  content: BlokComponentType<ImageType | LinkedImageType>[],
  link?: UrlType,
  link_text?: string,
  title?: string
}>) => {
  const content = (
    <Fragment>
      {title ? <Title align="center">{title}</Title> : null}
      <OverflowHandler>
        <Wrapper count={elements.length}>
          {elements &&
            elements.map((bodyBlok) =>
              renderComponent(
                bodyBlok,
                {
                  customPath: 'components/editable/ImageBlock.jsx',
                  key: bodyBlok._uid,
                  ...others
                },
                {
                  [componentKeys.IMAGE]: ({
                    blok: { image, ...props }
                  }: BlokComponentPropsType<ImageType>) => (
                    <Image src={image} {...props} />
                  ),
                  [componentKeys.LINKED_IMAGE]: LinkedImage
                }
              )
            )}
        </Wrapper>
      </OverflowHandler>
    </Fragment>
  );
  const hasLink = linkText && isValidLink(link);
  if (!hasLink) {
    return <Content>{content}</Content>;
  }
  return (
    <Positioner>
      <FilterContent>{content}</FilterContent>
      <Content>{content}</Content>
      <ButtonPositioner>
        <CTAButton
          component={Link}
          link={link}
          gaReference={`Image Block CTA; ${title}`}
        >
          {linkText}
        </CTAButton>
      </ButtonPositioner>
    </Positioner>
  );
};

export default ImageBlock;
